import { FormControl, Validators } from '@angular/forms';
import { AppForm } from '../../../../../../common/forms/app.form';
import { ParcelAppEntityParcelWrite, RoadOccupationStageAppEntityApplicationRead } from '../../../../../../openapi';
import { bothRequired, CustomValidator, fromToDate } from '../../../../../../common/forms/custom.validators';
import { calcDaysBetweenDates } from '../../../../../../services/helpers';

export function toStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): RoadOccupationStageFormValue {
  return {
    id: '',
    roadWidth: (v?.roadWidth || 0).toFixed(2).replace('.', ','),
    roadWidth2: (v?.roadWidth2 || 0).toFixed(2).replace('.', ','),
    roadWidth3: (v?.roadWidth3 || 0).toFixed(2).replace('.', ','),
    roadLength: (v?.roadLength || 0).toFixed(2).replace('.', ','),
    roadLength2: (v?.roadLength2 || 0).toFixed(2).replace('.', ','),
    roadLength3: (v?.roadLength3 || 0).toFixed(2).replace('.', ','),
    roadArea: (v?.roadArea || 0).toFixed(2).toString().replace('.', ','),
    roadArea2: (v?.roadArea2 || 0).toFixed(2).toString().replace('.', ','),
    roadArea3: (v?.roadArea3 || 0).toFixed(2).toString().replace('.', ','),
    sidewalkWidth: (v?.sidewalkWidth || 0).toFixed(2).replace('.', ','),
    sidewalkLength: (v?.sidewalkLength || 0).toFixed(2).replace('.', ','),
    sidewalkArea: (v?.sidewalkArea || 0).toFixed(2).replace('.', ','),
    otherElementsWidth: (v?.otherElementsWidth || 0).toFixed(2).replace('.', ','),
    otherElementsLength: (v?.otherElementsLength || 0).toFixed(2).replace('.', ','),
    otherElementsArea: (v?.otherElementsArea || 0).toFixed(2).replace('.', ','),
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,
    dayQuantity: v?.dayQuantity || 0,

    geometry: v?.geometry || '',

    provideRoadNumberAndName: !!v?.provideRoadNumberAndName,
    city: v?.city || '',

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    parcels: v?.parcels || []
  };
}

export interface RoadOccupationStageFormValue {
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number;

  geometry: string;

  provideRoadNumberAndName: boolean;
  city: string;

  roadName?: string;
  roadNumber?: string;
  street?: string;

  parcels: ParcelAppEntityParcelWrite[];

  roadWidth: string;
  roadWidth2: string;
  roadWidth3: string;

  roadLength: string;
  roadLength2: string;
  roadLength3: string;

  roadArea?: string;
  roadArea2?: string;
  roadArea3?: string;
  sidewalkWidth: string;
  sidewalkLength: string;
  sidewalkArea?: string;
  otherElementsWidth: string;
  otherElementsLength: string;
  otherElementsArea?: string;
}

export class RoadOccupationStageForm extends AppForm {

  constructor() {
    super({
      id: new FormControl('', { nonNullable: true }),
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())] }),
      endDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      dayQuantity: new FormControl(null, [Validators.required, Validators.maxLength(255)]),

      geometry: new FormControl(null),

      roadWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadWidth2: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadWidth3: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadLength2: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadLength3: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),
      roadArea2: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),
      roadArea3: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      sidewalkWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      sidewalkLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      sidewalkArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      otherElementsWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      otherElementsLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      otherElementsArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      }),

      provideRoadNumberAndName: new FormControl(true),
      city: new FormControl(
        '',
        { nonNullable: true, validators: [Validators.maxLength(255), Validators.required] }
      )
    }, {
      validators: [
        bothRequired('roadWidth', 'roadLength'),
        bothRequired('roadWidth2', 'roadLength2'),
        bothRequired('roadWidth3', 'roadLength3'),
        bothRequired('sidewalkWidth', 'sidewalkLength'),
        bothRequired('shoulderWidth', 'shoulderLength'),
        bothRequired('greeneryWidth', 'greeneryLength'),
        bothRequired('otherElementsWidth', 'otherElementsLength'),
        fromToDate('startDate', 'endDate')
      ]
    });
    this.provideRoadNumberAndNameChange(true);
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
    this.roadNumber?.setValue('');
    this.roadNumber?.markAsUntouched();
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));

      this.removeControl('street');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');
      this.addControl('street', new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  private get roadWidth(): FormControl {
    return this.get('roadWidth') as FormControl;
  }

  private get roadWidth2(): FormControl {
    return this.get('roadWidth2') as FormControl;
  }

  private get roadWidth3(): FormControl {
    return this.get('roadWidth3') as FormControl;
  }

  private get roadLength(): FormControl {
    return this.get('roadLength') as FormControl;
  }

  private get roadLength2(): FormControl {
    return this.get('roadLength2') as FormControl;
  }

  private get roadLength3(): FormControl {
    return this.get('roadLength3') as FormControl;
  }

  private get roadArea(): FormControl {
    return this.get('roadArea') as FormControl;
  }

  private get roadArea2(): FormControl {
    return this.get('roadArea2') as FormControl;
  }

  private get roadArea3(): FormControl {
    return this.get('roadArea3') as FormControl;
  }

  private get sidewalkWidth(): FormControl {
    return this.get('sidewalkWidth') as FormControl;
  }

  private get sidewalkLength(): FormControl {
    return this.get('sidewalkLength') as FormControl;
  }

  private get sidewalkArea(): FormControl {
    return this.get('sidewalkArea') as FormControl;
  }

  private get otherElementsWidth(): FormControl {
    return this.get('otherElementsWidth') as FormControl;
  }

  private get otherElementsLength(): FormControl {
    return this.get('otherElementsLength') as FormControl;
  }

  private get otherElementsArea(): FormControl {
    return this.get('otherElementsArea') as FormControl;
  }

  public calcRoadArea(): void {
    this.roadArea.setValue(
      this.calcArea(this.roadWidth.value, this.roadLength.value)
    );
  }

  public calcRoadArea2(): void {
    this.roadArea2.setValue(
      this.calcArea(this.roadWidth2.value, this.roadLength2.value)
    );
  }

  public calcRoadArea3(): void {
    this.roadArea3.setValue(
      this.calcArea(this.roadWidth3.value, this.roadLength3.value)
    );
  }

  public calcSidewalkArea(): void {
    this.sidewalkArea.setValue(
      this.calcArea(this.sidewalkWidth.value, this.sidewalkLength.value)
    );
  }

  public calcOtherElementsArea(): void {
    this.otherElementsArea.setValue(
      this.calcArea(
        this.otherElementsWidth.value,
        this.otherElementsLength.value
      )
    );
  }

  private calcArea(width?: string, length?: string): string {
    if (!width || !length) {
      return '';
    }
    width = (width || '0').replaceAll(',', '.').replaceAll(' ', '');
    length = (length || '0').replaceAll(',', '.').replaceAll(' ', '');
    const v = Number(width) * Number(length);
    return (v < 1 ? Math.ceil(v) : v).toFixed(2).replaceAll('.', ',');
  }

  public override get valid(): boolean {
    return super.valid && (!!this.roadArea.value || !!this.roadArea2.value || !!this.roadArea3.value || !!this.otherElementsArea.value || !!this.sidewalkArea.value);
  }

  public override patchFormValues(value: RoadOccupationStageFormValue): void {
    this.provideRoadNumberAndNameChange(value.provideRoadNumberAndName);
    super.patchFormValues({
      ...value,
      provideRoadNumberAndName: value.provideRoadNumberAndName
    });
  }

  public get values(): RoadOccupationStageFormValue {
    return {
      id: this.value.id,
      startDate: this.value.startDate,
      endDate: this.value.endDate,
      dayQuantity: this.dayQuantity.value,

      geometry: this.geometry.value,

      provideRoadNumberAndName: this.value?.provideRoadNumberAndName,
      city: this.value.city,

      roadName: this.roadName?.value || null,
      roadNumber: this.value?.roadNumber || null,
      street: this.street?.value || null,

      parcels: this.parcels.value,

      roadWidth: this.value.roadWidth,
      roadWidth2: this.value.roadWidth2,
      roadWidth3: this.value.roadWidth3,
      roadLength: this.value.roadLength,
      roadLength2: this.value.roadLength2,
      roadLength3: this.value.roadLength3,
      roadArea: this.roadArea.value,
      roadArea2: this.roadArea2.value,
      roadArea3: this.roadArea3.value,
      sidewalkWidth: this.value.sidewalkWidth,
      sidewalkLength: this.value.sidewalkLength,
      sidewalkArea: this.sidewalkArea.value,
      otherElementsWidth: this.value.otherElementsWidth,
      otherElementsLength: this.value.otherElementsLength,
      otherElementsArea: this.otherElementsArea.value
    };
  }
}
